/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {Box} from '@mui/material';
import Typography from '@mui/material/Typography';
import {
  DataGridPremium,
  gridClasses,
  GridColDef,
  type GridFilterModel,
  GridRowGroupingModel,
  GridRowsProp,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {useEffect, useMemo, useState} from 'react';
import {SuperChartClickContext} from './SuperChartClickContext';
import {SuperTableErrorOverlay} from './SuperTableErrorOverlay';
import {SuperTableFilter} from './SuperTableFilter';
import {SuperTableFooter} from './SuperTableFooter';
import {SuperTableOptionsModal} from './SuperTableOptionsModal';
import {SuperTableToolbar} from './SuperTableToolbar';
import {SuperTableOptions} from '../../hooks/useSuperTableOptions';
import {aggregationFunctions} from '../../services/super-table-utils';
import {useUserStore} from '../../stores/userStore';

const defaultHeight = '800px';
const defaultGridFilterModel: GridFilterModel = {items: []};

interface SuperTableProps<T extends GridValidRowModel> {
  columns: GridColDef[];
  height?: string;
  onApplyFilter: (filterModel?: GridFilterModel, groupingModel?: GridRowGroupingModel) => void;
  onRowClick?: (row: T, prefersModal: boolean) => void;
  rows: GridRowsProp<T>;
  noRowsLabel?: string;
  isLoading: boolean;
  isError: boolean;
  tableId: string;
  rowGroupingModel?: GridRowGroupingModel;
  onRowGroupingModelChange?: (model: GridRowGroupingModel) => void;
  options: SuperTableOptions;
  updateOptions: (options: SuperTableOptions) => void;
  isPartiallyLoading?: boolean;
  expectedTotalRows?: number | null;
}

export default function SuperTable<T extends GridValidRowModel>({
  columns,
  height = defaultHeight,
  onRowClick,
  rows,
  noRowsLabel,
  isLoading,
  isError,
  onApplyFilter,
  tableId,
  rowGroupingModel,
  onRowGroupingModelChange,
  options,
  updateOptions,
  isPartiallyLoading,
  expectedTotalRows,
}: SuperTableProps<T>) {
  const apiRef = useGridApiRef();
  const [filterModel, setFilterModel] = useState<GridFilterModel>(defaultGridFilterModel);
  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>(rowGroupingModel || []);
  const [isPendingApply, setIsPendingApply] = useState(false);
  const [filterButtonElement, setFilterButtonElement] = useState<HTMLButtonElement | null>(null);
  const userId = useUserStore((state) => state.userId);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);

  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // If reapplyFilter is enabled and current filter is default, apply stored lastFilter.
  useEffect(() => {
    if (!isLoading) {
      const filterModelIsEmpty = !filterModel?.items?.length;
      const grouppingModelIsEmpty = !groupingModel?.length;

      if (options.reapplyFilter && filterModelIsEmpty && options.lastFilter) {
        apiRef.current.setFilterModel(options.lastFilter);
        setFilterModel(options.lastFilter);
        setIsPendingApply(false);
      }
      if (options.reapplygrouping && grouppingModelIsEmpty && options.lastGrouping) {
        apiRef.current.setRowGroupingModel(options.lastGrouping);
        setGroupingModel(options.lastGrouping);
        setIsPendingApply(false);
      }
    }
  }, [options, apiRef, isLoading]);

  const initialState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          contactId: false,
        },
      },
    }),
    [],
  );

  useEffect(() => {
    if (isError) {
      apiRef.current?.setFilterModel(defaultGridFilterModel);
      apiRef.current?.setSortModel([]);
      apiRef.current?.setColumnVisibilityModel({});
      apiRef.current?.setAggregationModel({});
      apiRef.current?.setRowGroupingModel([]);
      setFilterModel(defaultGridFilterModel);
      onApplyFilter(defaultGridFilterModel);
      setIsPendingApply(false);
    }
  }, [isError, apiRef, onApplyFilter]);

  const handleRowClick = (params: any) => {
    // Ignore click events on auto generated rows such as Aggregation
    if (params.id?.toString().startsWith('auto-generated')) return;

    onRowClick?.(params, options.preferModal);
  };

  const chartMarkClickHandler = (id: string) => {
    const row = apiRef.current?.getRow(id);
    if (row) {
      onRowClick?.(row, options.preferModal);
    }
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
    setIsPendingApply(true);
  };

  const handleRowGroupingModelChange = (newGroupingModel: GridRowGroupingModel) => {
    setGroupingModel(newGroupingModel);
    setIsPendingApply(true);
  };

  const handleLoadFilter = (loadedFilterModel: GridFilterModel, loadedGroupingModel: GridRowGroupingModel) => {
    // reset the previous filter and grouping
    apiRef.current?.setFilterModel(defaultGridFilterModel);
    apiRef.current?.setRowGroupingModel([]);
    setFilterModel(defaultGridFilterModel);
    setGroupingModel([]);

    apiRef.current?.setFilterModel(loadedFilterModel);
    setFilterModel(loadedFilterModel);
    setGroupingModel(loadedGroupingModel);
    setIsPendingApply(true);
  };

  const handleApply = () => {
    onApplyFilter(filterModel);
    apiRef.current?.setRowGroupingModel(groupingModel);
    onRowGroupingModelChange?.(groupingModel);
    setIsPendingApply(false);
    if (options.reapplyFilter || options.reapplygrouping) {
      updateOptions({...options, lastFilter: filterModel, lastGrouping: groupingModel});
    }
  };

  const handleOnClear = () => {
    apiRef.current?.setFilterModel(defaultGridFilterModel);
    apiRef.current?.setRowGroupingModel([]);
    setFilterModel(defaultGridFilterModel);
    setGroupingModel([]);
    onApplyFilter(defaultGridFilterModel);
    setIsPendingApply(false);
    updateOptions({...options, lastFilter: null, lastGrouping: null});
  };

  const handleSaveOptions = (
    newRowsLimit: number,
    newPreferModal: boolean,
    newReapplyFilter: boolean,
    newDoubleClickToOpen: boolean,
    newReapplyGrouping: boolean,
  ) => {
    updateOptions({
      ...options,
      rowsLimit: newRowsLimit,
      preferModal: newPreferModal,
      reapplyFilter: newReapplyFilter,
      doubleClickToOpen: newDoubleClickToOpen,
      reapplygrouping: newReapplyGrouping,
    });
  };

  return (
    <SuperChartClickContext.Provider value={chartMarkClickHandler}>
      <Box
        sx={{
          height: isFullscreen ? '100vh' : height,
          display: 'flex',
          flexDirection: 'column',
          ...(isFullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            zIndex: 1300,
            overflow: 'auto',
            backgroundColor: 'var(--mui-palette-background-default)',
          }),
        }}
      >
        <DataGridPremium
          columns={columns}
          rows={isError ? [] : rows}
          initialState={{
            density: 'standard',
            ...initialState,
          }}
          onFilterModelChange={handleFilterModelChange}
          onRowClick={options.doubleClickToOpen ? undefined : handleRowClick}
          onRowDoubleClick={options.doubleClickToOpen ? handleRowClick : undefined}
          disableRowSelectionOnClick={!options.doubleClickToOpen}
          filterMode={'server'}
          apiRef={apiRef}
          sortingOrder={['desc', 'asc']}
          aggregationFunctions={aggregationFunctions}
          sx={{
            borderRadius: isFullscreen ? 0 : undefined,
            '& .MuiDataGrid-row:hover': {
              cursor: options.doubleClickToOpen ? 'default' : 'pointer',
            },
            ...(options.doubleClickToOpen
              ? {}
              : {
                  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                    outline: 'none',
                  },
                  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                    outline: 'none',
                  },
                }),
          }}
          slots={{
            toolbar: SuperTableToolbar,
            footer: SuperTableFooter,
            noRowsOverlay: isError ? SuperTableErrorOverlay : undefined,
            filterPanel: SuperTableFilter,
          }}
          slotProps={{
            footer: {rowCount: rows.length, expectedTotalRows, isPartiallyLoading},
            panel: {anchorEl: filterButtonElement},
            toolbar: {
              onApply: handleApply,
              onClear: handleOnClear,
              isPendingApply: isPendingApply,
              setFilterButtonEl: setFilterButtonElement,
              userId: userId!,
              tableId,
              filterModel,
              onLoadFilter: handleLoadFilter,
              groupingModel,
              onToggleFullscreen: toggleFullscreen,
              isFullscreen,
              onOpenOptions: () => setOptionsModalOpen(true),
              isPartiallyLoading,
            },
            noRowsOverlay: {onReset: handleOnClear},
          }}
          localeText={{
            noRowsLabel: isLoading ? 'Loading...' : noRowsLabel || 'Sorry, no matching records found.',
          }}
          onRowGroupingModelChange={handleRowGroupingModelChange}
          disableColumnFilter={isPartiallyLoading}
          disableColumnMenu={isPartiallyLoading}
          disableColumnSelector={isPartiallyLoading}
          disableRowGrouping={isPartiallyLoading}
        />
        {rows.length > options.rowsLimit && (
          <Box textAlign={'center'}>
            <Typography variant={'body2'} color={'text.secondary'}>
              Showing a maximum of {options.rowsLimit.toLocaleString()} items. Use filters to refine your results.
            </Typography>
          </Box>
        )}
      </Box>
      <SuperTableOptionsModal
        open={optionsModalOpen}
        currentRowsLimit={options.rowsLimit}
        currentPreferModal={options.preferModal}
        currentReapplyFilter={options.reapplyFilter}
        currentDoubleClickToOpen={options.doubleClickToOpen}
        currentReapplyGrouping={options.reapplygrouping}
        onClose={() => setOptionsModalOpen(false)}
        onSave={handleSaveOptions}
      />
    </SuperChartClickContext.Provider>
  );
}
